// Una hoja que sube desde abajo, para el editor en el teléfono —
// ADR-2026-10-07-el-calendario-en-el-telefono.
//
// Lo que en escritorio está a la vista (las acciones, la fecha, la vista previa) en el
// teléfono va detrás de una fila y se abre acá: LinkedIn programa en una hoja, Instagram y
// Notion ponen las acciones secundarias en una hoja con "Eliminar" en rojo al final.

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export function HojaMovil({
  abierta,
  titulo,
  onCerrar,
  pantallaCompleta = false,
  children,
}: {
  abierta: boolean;
  titulo: string;
  onCerrar: () => void;
  /** Ocupa casi todo el alto (la vista previa); si no, lo que mida el contenido. */
  pantallaCompleta?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!abierta) return;
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alTeclado);
    // Que la página de atrás no scrollee mientras la hoja está abierta.
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', alTeclado);
      document.body.style.overflow = antes;
    };
  }, [abierta, onCerrar]);

  if (!abierta) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end bg-black/40 md:hidden" onClick={onCerrar}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className={`w-full overflow-y-auto rounded-t-3xl bg-white px-4 pt-2 animate-ficha-entra ${
          pantallaCompleta ? 'h-[92vh]' : 'max-h-[85vh]'
        }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <span aria-hidden className="mx-auto mb-2 block h-1 w-10 rounded-full bg-neutral-200" />
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-brand-black">{titulo}</h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-8 w-8 place-items-center rounded-full bg-neutral-100 text-neutral-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
